import dayjs from 'dayjs';
import { alert } from '@/services/alert';
import { formatTime } from '@/services/date';
import { createReference, getReferenceString, isResource } from '@medplum/core';
import { Appointment, Encounter, PlanDefinition, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { useTimelineContext } from './useTimelineContext';
import { fetchBot } from '@/services/services';
import { convertToISOString } from '@/services/date';
import { orderTasksByPlanDefinition } from '@bariendo/lib/order-tasks';
import { CreateEncounterRequest } from '@bariendo/lib/charting';
import { useSearchParams } from 'react-router';
import { IV_THERAPY_APPOINTMENT_TYPE_CODE, TIMELINE_CREATE_ITEM_QUERY_PARAM } from '@bariendo/lib/constants';
import { DISPLAY_ORDER_EXTENSION } from '@bariendo/lib/extensions';

const CHART_APPOINTMENTS_PAST_DAYS_FILTER = import.meta.env.VITE_CHART_APPOINTMENTS_PAST_DAYS_FILTER ?? 14;
const CHART_APPOINTMENTS_FUTURE_DAYS_FILTER = import.meta.env.VITE_CHART_APPOINTMENTS_FUTURE_DAYS_FILTER ?? 3;

type AppointmentFieldset = {
  past: { label: string; value: string }[];
  future: { label: string; value: string }[];
};

function getDefaultTimes(): { startTime: Date; endTime: Date } {
  const startTime = new Date();
  const endTime = new Date(startTime);
  endTime.setHours(endTime.getHours() + 1);
  return { startTime, endTime };
}

function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) {
    return '--';
  }

  const date = dayjs(startDate).format('MMM D, YYYY');
  const startTime = formatTime(startDate);
  const endTime = formatTime(endDate);
  return `${date}, ${startTime}-${endTime}`;
}

/** `loadTemplates: false` skips the PlanDefinition read for roles that never chart an Encounter. */
export function useCreateEncounter({ loadTemplates = true }: { loadTemplates?: boolean } = {}) {
  const { patientContext, messageBoxContext, timelineContext } = useTimelineContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const patient = patientContext?.patient;
  const profile = useMedplumProfile() as Practitioner;
  const { setSentMessage, editingEncounterReference, setEditingEncounterReference } = messageBoxContext;
  const { pauseLiveUpdates } = timelineContext;
  const medplum = useMedplum();

  const [startTime, setStartTime] = useState<Date | null>(() => getDefaultTimes().startTime);
  const [endTime, setEndTime] = useState<Date | null>(getDefaultTimes().endTime);
  const [selectedPlanDefinition, setSelectedPlanDefinition] = useState<string | null>(null);
  const [planDefinitions, setPlanDefinitions] = useState<PlanDefinition[]>([]);
  const [isLoadingPlanDefinitions, setIsLoadingPlanDefinitions] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingEncounterDetails, setIsLoadingEncounterDetails] = useState(false);
  const [encounterSource, setEncounterSource] = useState<'create' | 'edit' | null>(null);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsFieldset, setAppointmentsFieldset] = useState<AppointmentFieldset>({ past: [], future: [] });
  const [selectedAppointment, setSelectedAppointment] = useState<string | null>(null);

  // Fetch PlanDefinitions on mount
  useEffect(() => {
    if (!loadTemplates) {
      setIsLoadingPlanDefinitions(false);
      return;
    }
    async function fetchPlanDefinitions() {
      try {
        setIsLoadingPlanDefinitions(true);

        const results = await medplum.searchResources('PlanDefinition', {
          status: 'active',
          _count: 1000,
        });

        const orderedResults = results.sort(
          (a, b) =>
            (a.extension?.find((extension) => extension.url === DISPLAY_ORDER_EXTENSION)?.valueInteger ?? 100) -
            (b.extension?.find((extension) => extension.url === DISPLAY_ORDER_EXTENSION)?.valueInteger ?? 100)
          // If the extension is not present, set item to 100 to move it to the last position in the list
        );

        setPlanDefinitions(orderedResults);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('create encounter:Error fetching PlanDefinitions:', error);
        alert.error('Failed to load care templates');
      } finally {
        setIsLoadingPlanDefinitions(false);
      }
    }

    fetchPlanDefinitions();
  }, [medplum, loadTemplates]);

  useEffect(() => {
    async function fetchAppointments() {
      if (!patient) return;

      setIsLoadingAppointments(true);

      // Filter appointments by date range (-14 days to +3 days from today)
      const startDate = dayjs().subtract(CHART_APPOINTMENTS_PAST_DAYS_FILTER, 'day').toISOString();
      const endDate = dayjs().add(CHART_APPOINTMENTS_FUTURE_DAYS_FILTER, 'day').toISOString();

      // Filter out appointments that don't have an encounter associated with it
      // or that have an encounter associated with it but is cancelled
      const filter = `
        date ge ${startDate} and date le ${endDate} and
        not (_has:Encounter:appointment:status pr true)
        or (_has:Encounter:appointment:status ne "cancelled")
      `;

      try {
        const appointments = await medplum.searchResources('Appointment', {
          patient: getReferenceString(patient),
          'status:not': 'cancelled,entered-in-error',
          'appointment-type:not': IV_THERAPY_APPOINTMENT_TYPE_CODE,
          _count: 1000,
          _filter: filter,
        });

        setAppointments(appointments);

        const parsedAppointments = appointments.reduce(
          (parsed, appointment) => {
            if (isResource(appointment, 'Appointment')) {
              const appointmentType = appointment.appointmentType?.coding?.[0]?.display ?? '--';
              const dateRange = formatDateRange(appointment.start, appointment.end);
              const fieldset = {
                value: getReferenceString(appointment),
                label: `${appointmentType} - ${dateRange}`,
              };

              if (dayjs(appointment.start).isBefore(dayjs())) {
                parsed.past.push(fieldset);
              } else {
                parsed.future.push(fieldset);
              }
            }

            return parsed;
          },
          { past: [], future: [] } as AppointmentFieldset
        );

        setAppointmentsFieldset(parsedAppointments);
      } catch (_error) {
        alert.error('Failed to load appointments');
      } finally {
        setIsLoadingAppointments(false);
      }
    }

    fetchAppointments();
  }, [medplum, patient]);

  // Clear form when patient changes
  useEffect(() => {
    const { startTime: defaultStart, endTime: defaultEnd } = getDefaultTimes();
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setSelectedPlanDefinition(null);
  }, [patient?.id]);

  // Open the encounter creation box and set the selected appointment based on the query param
  useEffect(() => {
    const action = searchParams.get(TIMELINE_CREATE_ITEM_QUERY_PARAM);
    const appointmentIdQueryParam = searchParams.get('appointment');

    if (action === 'chart' && appointmentIdQueryParam && !isLoadingAppointments) {
      setSelectedAppointment(`Appointment/${appointmentIdQueryParam}`);

      // reset the query param to avoid confusion if user click to add a new encounter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(TIMELINE_CREATE_ITEM_QUERY_PARAM);
      newSearchParams.delete('appointment');
      setSearchParams(newSearchParams);
    }
  }, [searchParams, isLoadingAppointments, setSearchParams]);

  // Set the start and end time based on the selected appointment
  useEffect(() => {
    if (selectedAppointment) {
      const selected = appointments.find((appointment) => getReferenceString(appointment) === selectedAppointment);

      setStartTime(dayjs(selected?.start).toDate());
      setEndTime(dayjs(selected?.end).toDate());
    }
  }, [appointments, selectedAppointment]);

  const resetEncounterState = useCallback(() => {
    setEncounter(null);
    setTasks([]);
    setEncounterSource(null);
  }, []);

  const clearEncounterDraft = useCallback(() => {
    resetEncounterState();
    setEditingEncounterReference(null);
  }, [resetEncounterState, setEditingEncounterReference]);

  const handleCreateEncounter = async () => {
    if (isCreating) return;

    if (!selectedPlanDefinition) {
      alert.error('Select a care plan first.');
      return;
    }

    if (!patient) {
      alert.error('Select a patient first.');
      return;
    }

    if (!startTime) {
      alert.error('Start time is required.');
      return;
    }

    if (!endTime) {
      alert.error('End time is required.');
      return;
    }

    if (startTime >= endTime) {
      alert.error('Start time must be before end time.');
      return;
    }

    try {
      setIsCreating(true);
      pauseLiveUpdates();

      const payload: CreateEncounterRequest = {
        patient: createReference(patient),
        practitioner: createReference(profile),
        planDefinition: { reference: selectedPlanDefinition },
        appointment: selectedAppointment ? ({ reference: selectedAppointment } as Reference<Appointment>) : undefined,
        startTime: convertToISOString(startTime),
        endTime: convertToISOString(endTime),
      };

      const response = await fetchBot<{
        encounter: Encounter;
        tasks: Task[];
        prefillErrors?: { taskId?: string; message: string }[];
      }>(medplum, 'create-encounter', payload);

      const { startTime: defaultStart, endTime: defaultEnd } = getDefaultTimes();
      setStartTime(defaultStart);
      setEndTime(defaultEnd);
      setSelectedPlanDefinition(null);
      setEncounter(response.encounter);
      setTasks(response.tasks);
      setEncounterSource('create');

      if (response.prefillErrors?.length) {
        alert.warning({
          title: 'Chart created with incomplete prefill',
          message: `${response.prefillErrors.length} questionnaire section(s) could not be prefilled and start blank. Fill them in by hand.`,
        });
      }
    } catch (error) {
      alert.error(`Couldn't create chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreating(false);
      setSentMessage(true);
    }
  };

  // Load encounter details when editing an existing encounter
  useEffect(() => {
    if (!editingEncounterReference) {
      return;
    }

    const encounterRefString = getReferenceString(editingEncounterReference);
    if (!encounterRefString) {
      alert.error('Invalid charting reference.');
      setEditingEncounterReference(null);
      return;
    }

    let isCancelled = false;
    resetEncounterState();
    setIsLoadingEncounterDetails(true);

    const fetchEncounterDetails = async (): Promise<void> => {
      try {
        // Retrieve all tasks using pagination to ensure we get all of them
        const allTasks: Task[] = [];
        for await (const taskPage of medplum.searchResourcePages('Task', {
          encounter: encounterRefString,
          _count: 1000,
        })) {
          allTasks.push(...taskPage);
        }

        const encounterResource = await medplum.readReference<Encounter>(editingEncounterReference);

        if (isCancelled) return;

        // Get PlanDefinition from the first task's basedOn field to order tasks
        let orderedTasks = allTasks;
        if (allTasks.length > 0 && allTasks[0].basedOn?.[0]) {
          try {
            const planDefinition = await medplum.readReference<PlanDefinition>(
              allTasks[0].basedOn[0] as Reference<PlanDefinition>
            );
            if (planDefinition) {
              orderedTasks = await orderTasksByPlanDefinition(medplum, allTasks, planDefinition);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to read PlanDefinition for task ordering:', error);
            // Continue with unordered tasks if PlanDefinition read fails
          }
        }

        setEncounter(encounterResource);
        setTasks(orderedTasks);
        setEncounterSource('edit');
      } catch (error) {
        if (isCancelled) return;
        alert.error(
          `Couldn't load chart: ${error instanceof Error ? error.message : 'Unknown error while loading chart'}`
        );
        resetEncounterState();
        setEditingEncounterReference(null);
      } finally {
        if (!isCancelled) {
          setIsLoadingEncounterDetails(false);
        }
      }
    };

    fetchEncounterDetails();

    return () => {
      isCancelled = true;
    };
  }, [editingEncounterReference, medplum, resetEncounterState, setEditingEncounterReference]);

  // Clear encounter state after exiting edit mode
  useEffect(() => {
    if (!editingEncounterReference) {
      setIsLoadingEncounterDetails(false);
      if (encounterSource === 'edit') {
        resetEncounterState();
      }
    }
  }, [editingEncounterReference, encounterSource, resetEncounterState]);

  return {
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    selectedPlanDefinition,
    setSelectedPlanDefinition,
    planDefinitions,
    isLoadingPlanDefinitions,
    isCreating,
    encounter,
    tasks,
    handleCreateEncounter,
    isLoadingEncounterDetails,
    isEditingExistingEncounter: encounterSource === 'edit',
    clearEncounterDraft,
    appointmentsFieldset,
    isLoadingAppointments,
    selectedAppointment,
    setSelectedAppointment,
  };
}
